import { Avatar } from '@cowbell/ui/core';
import { faFile } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { rehypePlugins, remarkPlugins, separateTables } from '../../markdown';
import type { AssistantMessageMeta } from '../../meta/types';
import {
  useMooConfig,
  useShellAgents,
  useShellSelection,
} from '../../provider';
import { A2UISurfaceList } from '../A2UISurfaceList';
import { ActionCardSlot } from '../ActionCardSlot/ActionCardSlot';
import { motion } from '../Motion';
import { SourcesButton } from '../Sources/SourcesButton';
import { SourcesProvider } from '../Sources/sources-context';
import { ThinkingBlock } from '../ThinkingBlock/ThinkingBlock';
import {
  TurnInterruptedNotice,
  TurnStatusLine,
} from '../TurnStatus/TurnStatus';
import {
  isThinkingInterrupted,
  isTurnInterrupted,
  type TurnClock,
} from '../TurnStatus/turn-status';
import styles from './ChatMarkdown.module.css';
import bubbleStyles from './MessageBubble.module.css';
import { CUSTOM_REACTIONS } from './custom-reactions';
import { MessageFeedback } from './MessageFeedback';
import { RollupBody } from './RollupBody';
import { WaitingVerb } from './WaitingVerb';
import { markdownComponents } from './components';

const AVATAR_SIZE = 'sm' as const;

export interface MessageBubbleProps {
  readonly role: 'user' | 'assistant';
  readonly text: string;
  readonly meta?: AssistantMessageMeta;
  readonly isStreaming?: boolean;
  /** Required for assistant messages; identifies the message for the
   *  feedback transport call. Omitted when no feedback affordance is wanted
   *  (e.g. user-role bubbles, transient streaming previews). */
  readonly messageId?: string;
  /** True when this is the most recent assistant message — forwarded to
   *  action-card renderers so historical cards can render disabled. */
  readonly isLastMessage?: boolean;
  /** False when this assistant message follows another assistant message
   *  in the same consecutive group — hides the avatar on non-first messages. */
  readonly isFirstInGroup?: boolean;
  /** The user turn that produced this message, forwarded to action-card
   *  renderers as the switch+replay fallback (OMNI-442 / OMNI-580). */
  readonly originatingQuery?: string;
  /** The user turn that answered this message, forwarded to action-card
   *  renderers so prompt cards stay answered across remounts (OMNI-702). */
  readonly followupUserText?: string;
  /** Timing of the in-flight turn. Set only on the message that is streaming;
   *  drives the live elapsed time and the quiet-server notice. */
  readonly turnClock?: TurnClock | null;
  /** Moo's emoji reaction to a user message, shown as a badge on the bubble. */
  readonly reaction?: string;
  /** Names of the files sent with a user message, shown above its text. */
  readonly attachments?: readonly string[];
}

export function MessageBubble(props: MessageBubbleProps) {
  if (props.role === 'user') {
    return (
      <UserBubble
        text={props.text}
        reaction={props.reaction}
        attachments={props.attachments}
      />
    );
  }
  return <AssistantTurn {...props} />;
}

function UserBubble({
  text,
  reaction,
  attachments = [],
}: Readonly<{
  text: string;
  reaction?: string;
  attachments?: readonly string[];
}>) {
  return (
    <div className={`${bubbleStyles.userTurn} ${motion.rise}`}>
      <div className={bubbleStyles.userBubble}>
        {attachments.length > 0 && (
          <ul className={bubbleStyles.userAttachments} aria-label="Attachments">
            {attachments.map((name, i) => (
              <li key={`${i}-${name}`} className={bubbleStyles.userAttachment}>
                <FontAwesomeIcon icon={faFile} aria-hidden />
                <span className={bubbleStyles.userAttachmentName}>{name}</span>
              </li>
            ))}
          </ul>
        )}
        {text}
        {reaction ? <ReactionBadge reaction={reaction} /> : null}
      </div>
    </div>
  );
}

/** Moo's reaction on a user message: a Unicode emoji, or a custom one drawn
 *  from an image (see CUSTOM_REACTIONS), which gets its own entrance. */
function ReactionBadge({ reaction }: Readonly<{ reaction: string }>) {
  const custom = CUSTOM_REACTIONS[reaction];
  return (
    <span
      className={bubbleStyles.userReaction}
      data-testid="user-reaction"
      data-custom={custom ? 'true' : undefined}
      role="img"
      aria-label={`Moo reacted ${custom?.label ?? reaction}`}
    >
      {custom ? (
        <img className={bubbleStyles.reactionImage} src={custom.src} alt="" />
      ) : (
        reaction
      )}
    </span>
  );
}

/**
 * One reply from Moo. A compact header row carries the avatar and whatever
 * Moo is doing (or did) — the thinking rollup, a live "Thinking" before any
 * of it arrives, or just the agent's name — and the answer runs the full
 * width of the panel beneath it, with no box around it.
 */
function AssistantTurn({
  text,
  meta,
  isStreaming = false,
  messageId,
  isLastMessage = false,
  isFirstInGroup = true,
  originatingQuery,
  followupUserText,
  turnClock = null,
}: MessageBubbleProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  const thinkingBlocks = meta
    ? [
        ...meta.subAskThinkings.map((t, idx) => ({
          key: t.subAskId ?? `sub-ask-${idx}`,
          thinking: t,
        })),
        ...(meta.thinking ? [{ key: 'main', thinking: meta.thinking }] : []),
      ]
    : [];
  const hasThinking = thinkingBlocks.length > 0;
  const isWaiting = isStreaming && !text && !hasThinking;
  const showInterrupted = isTurnInterrupted(meta, isStreaming);
  const showHeader = isFirstInGroup || hasThinking || isWaiting;

  // Sources pill and the rating/copy/print cluster share one footer row at
  // the bottom of the reply, so the row renders whenever either half has
  // something to show.
  const showSources =
    meta !== undefined &&
    !isStreaming &&
    (meta.sources.length > 0 || Boolean(meta.sourcesSummary));
  const showFeedback = Boolean(messageId && text && !isStreaming);

  return (
    <article
      className={bubbleStyles.assistantTurn}
      data-streaming={isStreaming}
      data-last={isLastMessage}
      aria-busy={isStreaming}
    >
      {showHeader && (
        <div className={bubbleStyles.turnHeader}>
          <span className={bubbleStyles.turnAvatar} data-live={isStreaming}>
            {isFirstInGroup && <AssistantAvatar />}
          </span>
          <div className={bubbleStyles.turnHeaderBody}>
            {thinkingBlocks.map(({ key, thinking }) => (
              <ThinkingBlock
                key={key}
                title={thinking.title}
                label={thinking.label}
                rows={thinking.rows}
                skillId={thinking.skillId}
                isComplete={thinking.isComplete}
                pendingReasoning={thinking.pendingReasoning}
                isInterrupted={isThinkingInterrupted(thinking, isStreaming)}
                liveStartedAt={turnClock?.startedAt}
                durationKey={messageId ? `${messageId}:${key}` : undefined}
                isAnswering={Boolean(text)}
              />
            ))}
            {!hasThinking && <HeaderLabel isWaiting={isWaiting} />}
          </div>
        </div>
      )}

      <div
        className={bubbleStyles.assistantBody}
        data-testid="assistant-content"
      >
        <RollupBody
          enabled={!isLastMessage && !isStreaming}
          renderFooter={(foldToggle) =>
            (foldToggle || showSources || showFeedback) && (
              <div
                className={`${bubbleStyles.messageFooter} ${motion.fadeIn}`}
                data-testid="message-footer"
              >
                {foldToggle}
                {meta && showSources && (
                  <SourcesButton
                    sources={meta.sources}
                    summary={meta.sourcesSummary}
                  />
                )}

                {/* Feedback affordance only after the message is fully streamed
                 *  and has actual content. Prefer the server messageId from
                 *  `data-done` over the AI SDK's client-generated `messageId` —
                 *  the feedback API is keyed by the server's persisted id. Falls
                 *  back to the prop for historical messages, which seed
                 *  `UIMessage.id` from the server id directly. */}
                {showFeedback && (
                  <div
                    className={bubbleStyles.messageFooterActions}
                    data-testid="feedback-wrapper"
                  >
                    <MessageFeedback
                      messageId={meta?.serverMessageId ?? messageId ?? ''}
                      initialRating={meta?.feedbackRating ?? null}
                      text={text}
                      contentRef={contentRef}
                    />
                  </div>
                )}
              </div>
            )
          }
        >
          {/* Print reuses this container's innerHTML (see MessageFeedback's
           *  handlePrint), so everything a printed message should include —
           *  markdown, action cards, A2UI chart surfaces — has to live inside
           *  the same ref'd node. The footer row below is deliberately outside
           *  it: PRINT_CSS already strips bare <button>s, so neither the
           *  sources pill nor the action icons ever reached the printed page. */}
          <div
            className={styles.mdContent}
            ref={contentRef}
            data-streaming={isStreaming && Boolean(text)}
          >
            {text && (
              <SourcesProvider sources={meta?.sources ?? []}>
                <div className={styles.markdown}>
                  <ReactMarkdown
                    remarkPlugins={remarkPlugins}
                    rehypePlugins={rehypePlugins}
                    components={markdownComponents}
                  >
                    {separateTables(text)}
                  </ReactMarkdown>
                </div>
              </SourcesProvider>
            )}

            {/* V1 parity: action cards only mount after the stream finishes —
             *  half-streamed payloads can still mutate, and the server
             *  messageId from `data-done` may not have arrived yet (the
             *  AI SDK's client id is unsafe for executeAction round-trips). */}
            {messageId &&
              !isStreaming &&
              meta?.actions?.map((action, idx) => (
                <ActionCardSlot
                  key={`${action.type}:${action.subType ?? ''}:${idx}`}
                  action={action}
                  messageId={meta.serverMessageId ?? messageId}
                  correlationId={meta.correlationId}
                  isLastMessage={isLastMessage}
                  originatingQuery={originatingQuery}
                  followupUserText={followupUserText}
                />
              ))}

            {/* A2UI surfaces — render adjacent to action cards. The list itself
             *  gates rendering on `streamingComplete`; `data-done` is what sets
             *  `meta.correlationId`, so a non-null correlation id is the
             *  cleanest "stream has finalized" signal available at this
             *  layer. */}
            {messageId && meta && meta.a2uiMessages.length > 0 && (
              <A2UISurfaceList
                messageId={meta.serverMessageId ?? messageId}
                envelopes={meta.a2uiMessages}
                correlationId={meta.correlationId}
                streamingComplete={meta.correlationId !== null}
                isLastMessage={isLastMessage}
              />
            )}
          </div>
        </RollupBody>

        {isStreaming && turnClock && <TurnStatusLine clock={turnClock} />}

        {showInterrupted && <TurnInterruptedNotice />}
      </div>
    </article>
  );
}

/** The header's words when no thinking block owns them: a live "Thinking"
 *  before anything has arrived, otherwise the agent's name. */
function HeaderLabel({ isWaiting }: Readonly<{ isWaiting: boolean }>) {
  const agent = useActiveAgent();
  if (isWaiting) {
    return (
      <span className={bubbleStyles.headerText} aria-label="Thinking...">
        <WaitingVerb />
      </span>
    );
  }
  return <span className={bubbleStyles.agentName}>{agent?.name ?? 'Moo'}</span>;
}

function useActiveAgent() {
  const { agentId } = useShellSelection();
  const agents = useShellAgents();
  return agentId ? agents.byId[agentId] ?? null : null;
}

/** Avatar shown next to assistant messages. Prefers the host's
 *  `renderAgentAvatar` slot when an agent is selected; otherwise falls
 *  back to a Mantine Avatar with the agent's first letter (or "M" when no
 *  agent is known). Exported so MessageList's waiting tail can render the
 *  same avatar while the reply has not started. */
export function AssistantAvatar() {
  const { renderAgentAvatar } = useMooConfig();
  const agent = useActiveAgent();

  const hostAvatar = agent
    ? renderAgentAvatar?.(agent, { size: AVATAR_SIZE })
    : null;
  if (hostAvatar) return <>{hostAvatar}</>;

  const fallbackInitial = agent?.name?.charAt(0).toUpperCase() ?? 'M';
  return (
    <Avatar
      size={AVATAR_SIZE}
      radius="var(--moo-radius)"
      variant="filled"
      color="#0057a8"
      src={agent?.iconUrl}
      aria-label="Assistant avatar"
      style={{ flexShrink: 0 }}
    >
      {fallbackInitial}
    </Avatar>
  );
}
